import {
    IGraphicInfo,
    IGraphicLineInfo,
} from '@src/slide/domain/ui-model/element-modeles/property/graphic-info/graphic-info.interface';
import {
    ICTPresetGeometry2D,
    STShapeType,
    ArrayOpType,
    ICTGeomGuide,
    ICTGeomGuideList,
    DeltaOf,
} from '@tencent/slide-sdk-model';
import lowerFirst from 'lodash/lowerFirst';
import { DeepPartial, DeepReadonly } from 'ts-essentials';

export const prstGeomGdNames = {
    frame: ['adj1'],
    mathPlus: ['adj1'],
    mathMinus: ['adj1'],
    mathMultiply: ['adj1'],
    hexagon: ['adj', 'vf'],
    star5: ['adj', 'hf', 'vf'],
    star6: ['adj', 'hf'],
    star7: ['adj', 'hf', 'vf'],
    star10: ['adj', 'hf'],
};

export class GraphicInfoConverter {
    /**
     * 数据3.0转换接口
     */
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTPresetGeometry2D>>,
        uiData: IGraphicInfo,
    ): void {
        if (sdkData.prst !== undefined && sdkData.prst !== null) {
            uiData.bgShape = lowerFirst(STShapeType[sdkData.prst]);
        }
        if (sdkData.avLst?.gd) {
            uiData.controlValues = GraphicInfoConverter.convertAvListFromSdkToUi(sdkData.avLst?.gd);
        } else {
            delete uiData.controlValues;
        }
    }

    /**
     * TODO 后续应该和graphicInfo统一，不需要bgLine
     * @param sdkData
     * @param uiData
     */
    public static convertGraphicLineFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTPresetGeometry2D>>,
        uiData: IGraphicLineInfo,
    ): void {
        if (sdkData.prst !== undefined && sdkData.prst !== null) {
            uiData.bgLine = lowerFirst(STShapeType[sdkData.prst]);
        }
        if (sdkData.avLst?.gd?.length) {
            uiData.controlValues = GraphicInfoConverter.convertAvListFromSdkToUi(sdkData.avLst?.gd);
        } else {
            delete uiData.controlValues;
        }
    }

    public static convertPartialDataFromUiToSdk(partialUiData: DeepPartial<IGraphicInfo>):
    DeltaOf<ICTPresetGeometry2D> {
        const toSdkData: DeltaOf<ICTPresetGeometry2D> = {};
        if (partialUiData.bgShape !== undefined) {
            toSdkData.prst = STShapeType[partialUiData.bgShape];
        }
        const { bgShape, controlValues } = partialUiData;
        // 部分图形的 name 有预设值，需要提前修改
        if (bgShape && prstGeomGdNames[bgShape] && controlValues) {
            const gd = controlValues.map((controlValue, index) => ({
                fmla: `val ${Math.floor((controlValue ?? 0) * 100000)}`,
                name: prstGeomGdNames[bgShape][index] ?? `adj${index}`,
            }));
            toSdkData.avLst = {
                gd,
                gdOp: { opType: ArrayOpType.Set },
            };
            return toSdkData;
        }
        // 非 Connector 且只有一个控制点，name 为 adj
        if (!bgShape?.includes('Connector') && controlValues?.length === 1) {
            toSdkData.avLst = {
                gd: [
                    {
                        fmla: `val ${Math.floor((controlValues[0] ?? 0) * 100000)}`,
                        name: 'adj',
                    },
                ],
                gdOp: { opType: ArrayOpType.Set },
            };
            return toSdkData;
        }
        // 普通控制点转换
        if (controlValues) {
            toSdkData.avLst = GraphicInfoConverter.convertAvListFromUiToSdk(controlValues as Array<number>);
        }
        return toSdkData;
    }

    /**
     * 是否为连接线类型
     * @param stShapeType
     * @private
     */
    private static isConnector(stShapeType: STShapeType) {
        const connectorTypes = [
            STShapeType.straightConnector1,
            STShapeType.bentConnector2,
            STShapeType.bentConnector3,
            STShapeType.bentConnector4,
            STShapeType.bentConnector5,
            STShapeType.curvedConnector2,
            STShapeType.curvedConnector3,
            STShapeType.curvedConnector4,
            STShapeType.curvedConnector5,
        ];
        if (connectorTypes.includes(stShapeType)) return true;
        return false;
    }

    private static convertAvListFromSdkToUi(avList: DeepReadonly<DeepPartial<ICTGeomGuide[]>>): Array<number> {
        const controlValues: Array<number> = [];
        for (let i = 0, len = avList.length; i < len; i++) {
            const fmla = avList[i]?.fmla;
            if (!fmla) continue;
            const afterSplit = fmla?.split(' ');
            if (afterSplit.length > 1) {
                controlValues.push((Number(afterSplit[1]) || 0) / 100000);
            }
        }
        return controlValues;
    }

    private static convertAvListFromUiToSdk(controlValues: Array<number>): DeltaOf<ICTGeomGuideList> {
        const gdArray: Array<any> = [];
        for (let i = 0; i < controlValues.length; i++) {
            const item = {
                fmla: `val ${Math.floor(controlValues[i] * 100000) || 0}`,
                name: `adj${i + 1}`,
            };
            gdArray.push(item);
        }
        return {
            gd: gdArray,
            gdOp: { opType: ArrayOpType.Set },
        };
    }
}
